import {
    bindTrustedActivate,
    buildSlotUrl,
    decodePacket,
    isTrustedActionEvent,
    openTrustedUrl
} from "./slot-codec";
import { init as initPictureFallback } from "../picture-fallback";

const INLINE_SVG_SPRITE = `<svg aria-hidden="true" style="position:absolute;width:0;height:0;overflow:hidden;pointer-events:none" xmlns="http://www.w3.org/2000/svg"><symbol id="image-placeholder-icon" viewBox="0 0 47 38" fill="none"><path d="M40.5189 0H6.48105C2.91895 0 0 2.91169 0 6.46494V31.5351C0 35.0883 2.91895 38 6.48105 38H40.5189C44.0811 38 47 35.0883 47 31.5351V6.46494C47 2.91169 44.0811 0 40.5189 0ZM32.1084 8.09351C34.2358 8.09351 35.9674 9.82078 35.9674 11.9429C35.9674 14.0649 34.2358 15.7922 32.1084 15.7922C29.9811 15.7922 28.2495 14.0649 28.2495 11.9429C28.2 9.82078 29.9316 8.09351 32.1084 8.09351ZM36.5116 30.5974H9.74632C8.41053 30.5974 7.56947 29.1169 8.31158 27.9818L17.5632 13.7688C18.2558 12.7325 19.7895 12.7325 20.4326 13.8182L26.4189 23.639C26.6663 24.0831 27.26 24.1325 27.6063 23.787L29.6842 21.813C30.3768 21.1714 31.4158 21.1714 32.1084 21.8623L37.7484 27.7844C38.7874 28.8208 38.0453 30.5974 36.5116 30.5974Z" fill="currentColor"/></symbol><symbol id="windows-close-icon" viewBox="0 0 32 32" width="16px" height="16px" fill="#121212"><path d="M 7.21875 5.78125 L 5.78125 7.21875 L 14.5625 16 L 5.78125 24.78125 L 7.21875 26.21875 L 16 17.4375 L 24.78125 26.21875 L 26.21875 24.78125 L 17.4375 16 L 26.21875 7.21875 L 24.78125 5.78125 L 16 14.5625 Z"/></symbol><symbol id="close-icon" viewBox="0 0 10 10" stroke="none"><path fill="currentColor" d="M4.95862 3.64077L7.83523 0.76416L9.00262 1.93155L6.12601 4.80816L9.19459 7.87674L7.87674 9.19459L4.80816 6.12601L1.93155 9.00262L0.76416 7.83523L3.64077 4.95862L0.805669 2.12352L2.12352 0.805669L4.95862 3.64077Z"/></symbol></svg>`;

const SHADOW_INTERNAL_CSS = `
:host { display: block; width: 100%; max-width: 100%; box-sizing: border-box; }
*, *::before, *::after { box-sizing: border-box; }
.pl-s-root { width: 100%; position: relative; }
.pl-s-root .pl-card-box--top, .pl-s-root .info-block--header { margin-left: auto; margin-right: auto; }
.pl-s-root .pl-card-box:not(.btn) { text-decoration: none; color: inherit; }
.pl-s-root .pl-promo-card:not(.btn) { text-decoration: none; color: var(--text-color); }
.pl-s-root .pl-sys-btn:not(.accent) { color: var(--link-active-color); }
.pl-s-root.dark .pl-card-box, .pl-s-root.dark .info-block { background-color: var(--header-bg) !important; }
.pl-s-root .pl-promo-card figure { margin: 0; }
.pl-s-root .pl-promo-card figure.mr-2 { margin-right: 10px; }
.pl-s-root .pl-promo-card figure.mr-4 { margin-right: 20px; }
.pl-s-root .pl-promo-card figure.mb-4 { margin-bottom: 20px; }
.pl-s-root .pl-sys-card__content, .pl-s-root .window-container__content, .pl-s-root [data-p-d], .pl-s-root [data-p-m] { position: relative; }
.pl-s-root [data-p-d] img, .pl-s-root [data-p-m] img { max-width: 100%; height: auto; display: block; }
[data-p-badge] {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    width: fit-content;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(128, 128, 128, 0.12);
    color: var(--c-secondary, #7d8597);
    font-size: 11px;
    line-height: 14px;
    cursor: pointer;
    user-select: none;
    position: absolute;
    top: 7px;
    left: 12px;
    z-index: 4;
}
[data-p-badge][data-p-overlay="1"] {
    top: 8px;
    left: 12px;
    background: rgba(18, 18, 18, 0.62);
    color: #e6edf3;
    backdrop-filter: blur(2px);
}
[data-p-badge] canvas {
    display: block;
    pointer-events: none;
}
[data-p-ord-pop] {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 190px;
    max-width: 280px;
    padding: 8px 10px;
    border-radius: 6px;
    background: var(--block-bg, #ffffff);
    color: var(--text-color, #222222);
    border: 1px solid var(--light-border-color, rgba(128, 128, 128, 0.25));
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
    font-size: 11px;
    line-height: 15px;
    z-index: 10;
    cursor: default;
}
[data-p-ord-pop] [data-p-copy] {
    margin-top: 6px;
    padding: 3px 8px;
    border-radius: 4px;
    border: 1px solid var(--accent-color, #378ff6);
    background: transparent;
    color: var(--accent-color, #378ff6);
    font-size: 11px;
    cursor: pointer;
}
`;

const TRUSTED_LINK_RE = /\/assets\/(?:service|main|profile\/profile\.main|account\/account)\.min\.css(?:\?|$)/;

const activeShadows = new Set();
const shadowHandles = new WeakMap();
const boundPromoHosts = new WeakSet();
let themeListenerBound = false;
let globalStyleObserver = null;
let healRafId = null;

function getBadgeWord() {
    return String.fromCharCode(1056, 1077, 1082, 1083, 1072, 1084, 1072);
}

function isTrustedStylesheetNode(node) {
    if (!node) {
        return false;
    }
    if (node.nodeName === "LINK" && node.rel === "stylesheet" && !node.disabled) {
        const href = node.getAttribute("href") || "";

        return TRUSTED_LINK_RE.test(href);
    }

    return false;
}

function isDarkActive() {
    return typeof document !== "undefined"
        && document.documentElement
        && document.documentElement.classList.contains("dark");
}

function syncDarkImagesInRoot(root, isDark) {
    if (!root) {
        return;
    }
    root.querySelectorAll("[data-dark-src]").forEach((img) => {
        if (!img.dataset.lightSrc) {
            img.dataset.lightSrc = img.getAttribute("src") || "";
        }
        const nextSrc = isDark ? img.dataset.darkSrc : img.dataset.lightSrc;
        if (nextSrc) {
            img.src = nextSrc;
        }
    });
}

function pruneDisconnectedShadows() {
    activeShadows.forEach((entry) => {
        if (!entry.host || !entry.host.isConnected) {
            destroyEntry(entry);
        }
    });
}

function destroyEntry(entry) {
    if (!entry) {
        return;
    }
    entry.active = false;
    if (entry.heightLocked && entry.host) {
        entry.heightLocked = false;
        entry.host.style.minHeight = "";
    }
    if (entry.resizeObserver) {
        entry.resizeObserver.disconnect();
        entry.resizeObserver = null;
    }
    if (entry.attrObserver) {
        entry.attrObserver.disconnect();
        entry.attrObserver = null;
    }
    activeShadows.delete(entry);
    entry.clonedNodes.clear();
    entry.badges.clear();
}

function syncHeadStylesToEntry(entry) {
    if (typeof document === "undefined" || !document.head) {
        return;
    }

    const headNodes = Array.from(document.head.children).filter(isTrustedStylesheetNode);
    let pendingLinks = 0;

    const releaseHeightLock = () => {
        pendingLinks = Math.max(0, pendingLinks - 1);
        if (pendingLinks === 0 && entry.heightLocked) {
            entry.heightLocked = false;
            entry.host.style.minHeight = "";
        }
    };

    headNodes.forEach((orig) => {
        if (!entry.clonedNodes.has(orig)) {
            const clone = orig.cloneNode(true);
            pendingLinks++;
            clone.addEventListener("load", releaseHeightLock, { once: true });
            clone.addEventListener("error", releaseHeightLock, { once: true });
            entry.shadow.insertBefore(clone, entry.root);
            entry.clonedNodes.set(orig, clone);
        }
    });

    if (pendingLinks === 0 && entry.heightLocked) {
        entry.heightLocked = false;
        entry.host.style.minHeight = "";
    } else if (pendingLinks > 0 && entry.heightLocked) {
        window.setTimeout(() => {
            if (entry.heightLocked) {
                entry.heightLocked = false;
                entry.host.style.minHeight = "";
            }
        }, 250);
    }
}

function scheduleHealAll() {
    if (healRafId !== null || typeof window === "undefined") {
        return;
    }
    healRafId = window.requestAnimationFrame(() => {
        healRafId = null;
        pruneDisconnectedShadows();
        activeShadows.forEach((entry) => checkAndHeal(entry));
    });
}

function ensureThemeListener() {
    if (typeof document === "undefined") {
        return;
    }
    if (!themeListenerBound) {
        themeListenerBound = true;
        document.addEventListener("theme.toggled", (event) => {
            pruneDisconnectedShadows();
            const isDark = event && event.detail && typeof event.detail.enabled === "boolean"
                ? event.detail.enabled
                : isDarkActive();

            activeShadows.forEach((entry) => {
                entry.root.classList.toggle("dark", isDark);
                syncDarkImagesInRoot(entry.root, isDark);
                entry.badges.forEach((drawFn) => drawFn(true));
            });
        });
    }
    if (!globalStyleObserver && typeof MutationObserver !== "undefined" && document.documentElement) {
        globalStyleObserver = new MutationObserver((mutations) => {
            let shouldSchedule = false;
            for (let i = 0; i < mutations.length; i++) {
                const m = mutations[i];
                if (m.type === "childList") {
                    if (m.target && m.target.nodeName === "STYLE") {
                        shouldSchedule = true;
                    }
                    for (let j = 0; j < m.addedNodes.length; j++) {
                        const n = m.addedNodes[j];
                        if (!n) {
                            continue;
                        }
                        if (n.nodeName === "STYLE") {
                            shouldSchedule = true;
                        } else if (n.nodeName === "LINK") {
                            n.addEventListener("load", scheduleHealAll, { once: true });
                            shouldSchedule = true;
                        } else if (typeof n.querySelectorAll === "function") {
                            const nestedLinks = n.querySelectorAll("link");
                            if (nestedLinks.length > 0) {
                                nestedLinks.forEach((lnk) => lnk.addEventListener("load", scheduleHealAll, { once: true }));
                                shouldSchedule = true;
                            }
                            if (n.querySelector("style")) {
                                shouldSchedule = true;
                            }
                        }
                    }
                } else if (m.type === "characterData") {
                    const p = m.target && m.target.parentNode;
                    if (p && p.nodeName === "STYLE") {
                        shouldSchedule = true;
                    }
                } else if (m.type === "attributes" && m.target) {
                    if (m.target.nodeName === "LINK") {
                        m.target.addEventListener("load", scheduleHealAll, { once: true });
                        shouldSchedule = true;
                    } else if (m.target.nodeName === "STYLE") {
                        shouldSchedule = true;
                    }
                }
            }
            if (shouldSchedule) {
                scheduleHealAll();
            }
        });
        globalStyleObserver.observe(document.documentElement, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ["disabled", "media", "href"]
        });
    }
}

function isHiddenByStyle(el) {
    if (!el || typeof window === "undefined" || !window.getComputedStyle) {
        return false;
    }
    const cs = window.getComputedStyle(el);

    return el.hidden === true
        || cs.display === "none"
        || cs.visibility === "hidden"
        || parseFloat(cs.opacity || "1") < 0.05;
}

function checkAndHeal(entry) {
    if (!entry || !entry.active || entry.healing || entry.healAttempts >= 3) {
        return;
    }
    const hostEl = entry.host;
    if (!hostEl || !hostEl.isConnected) {
        return;
    }

    const parentEl = hostEl.parentElement;
    if (parentEl && parentEl !== document.body) {
        if (isHiddenByStyle(parentEl) || parentEl.getBoundingClientRect().width < 8) {
            return;
        }
    }

    if (!isHiddenByStyle(hostEl)) {
        return;
    }

    entry.healAttempts++;
    entry.healing = true;
    hostEl.hidden = false;
    hostEl.style.setProperty("display", "block", "important");
    hostEl.style.setProperty("visibility", "visible", "important");
    hostEl.style.setProperty("opacity", "1", "important");
    Promise.resolve().then(() => {
        entry.healing = false;
    });
}

export function mountSlotShadow(hostEl, options = {}) {
    pruneDisconnectedShadows();

    if (!hostEl || typeof hostEl.attachShadow !== "function") {
        return {
            root: hostEl,
            card: hostEl,
            setActive() {},
            destroy() {}
        };
    }

    const existingHandle = shadowHandles.get(hostEl);
    if (existingHandle) {
        return existingHandle;
    }

    const initialRect = hostEl.getBoundingClientRect();
    const originalInlineStyle = hostEl.getAttribute("style");
    const originalClassName = hostEl.className;
    const originalRole = hostEl.getAttribute("role");
    const originalTabIndex = hostEl.getAttribute("tabindex");

    let shadow;
    try {
        shadow = hostEl.attachShadow({ mode: "closed" });
    } catch (e) {
        return {
            root: hostEl,
            card: hostEl,
            setActive() {},
            destroy() {}
        };
    }

    const baseStyle = document.createElement("style");
    baseStyle.textContent = SHADOW_INTERNAL_CSS;
    shadow.appendChild(baseStyle);

    const spriteHolder = document.createElement("div");
    spriteHolder.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";
    spriteHolder.innerHTML = INLINE_SVG_SPRITE;
    if (spriteHolder.firstElementChild) {
        shadow.appendChild(spriteHolder.firstElementChild);
    }

    const root = document.createElement("div");
    root.className = "pl-s-root";
    if (isDarkActive()) {
        root.classList.add("dark");
    }
    if (hostEl.closest(".light-bg")) {
        root.classList.add("light-bg");
    }
    if (hostEl.closest(".stretch-info")) {
        root.classList.add("stretch-info");
    }
    if (hostEl.closest(".ugc")) {
        root.classList.add("ugc");
    }

    let card = root;
    if (options.wrapSelf) {
        card = document.createElement("div");
        if (originalClassName) {
            card.className = originalClassName;
            hostEl.removeAttribute("class");
        }
        if (originalInlineStyle) {
            card.setAttribute("style", originalInlineStyle);
            hostEl.removeAttribute("style");
        }
        if (originalRole) {
            card.setAttribute("role", originalRole);
            hostEl.removeAttribute("role");
        }
        if (originalTabIndex) {
            card.setAttribute("tabindex", originalTabIndex);
            hostEl.removeAttribute("tabindex");
        }
        while (hostEl.firstChild) {
            card.appendChild(hostEl.firstChild);
        }
        root.appendChild(card);
    } else {
        while (hostEl.firstChild) {
            root.appendChild(hostEl.firstChild);
        }
    }

    let heightLocked = false;
    if (initialRect && initialRect.height > 16) {
        hostEl.style.minHeight = `${Math.round(initialRect.height)}px`;
        heightLocked = true;
    }

    shadow.appendChild(root);
    syncDarkImagesInRoot(root, isDarkActive());
    root.querySelectorAll("picture img").forEach((img) => initPictureFallback(img));

    const entry = {
        host: hostEl,
        shadow,
        root,
        card,
        clonedNodes: new Map(),
        badges: new Set(),
        active: options.active !== false,
        healing: false,
        healAttempts: 0,
        heightLocked,
        resizeObserver: null,
        attrObserver: null
    };

    activeShadows.add(entry);
    ensureThemeListener();
    syncHeadStylesToEntry(entry);

    if (typeof ResizeObserver !== "undefined") {
        entry.resizeObserver = new ResizeObserver(() => {
            checkAndHeal(entry);
        });
        entry.resizeObserver.observe(hostEl);
    }

    if (typeof MutationObserver !== "undefined") {
        entry.attrObserver = new MutationObserver(() => {
            checkAndHeal(entry);
        });
        entry.attrObserver.observe(hostEl, {
            attributes: true,
            attributeFilter: ["style", "class", "hidden"]
        });
    }

    const handle = {
        root,
        card,
        registerBadge(drawFn) {
            if (typeof drawFn === "function") {
                entry.badges.add(drawFn);
            }
        },
        setActive(value) {
            entry.active = Boolean(value);
            if (!entry.active) {
                entry.heightLocked = false;
                hostEl.style.minHeight = "";
            } else {
                checkAndHeal(entry);
            }
        },
        destroy() {
            destroyEntry(entry);
        }
    };

    shadowHandles.set(hostEl, handle);

    return handle;
}

export function attachCanvasBadge(targetEl, meta = {}) {
    if (!targetEl || typeof document === "undefined") {
        return null;
    }
    if (!meta.erid && !meta.ord) {
        return null;
    }

    const existing = targetEl.querySelector("[data-p-badge]");
    if (existing) {
        return existing;
    }

    const labelText = getBadgeWord();
    const badgeWrap = document.createElement("div");
    badgeWrap.setAttribute("data-p-badge", "1");
    badgeWrap.setAttribute("role", "button");
    badgeWrap.setAttribute("tabindex", "0");
    badgeWrap.setAttribute("aria-label", labelText);
    if (meta.overlay) {
        badgeWrap.setAttribute("data-p-overlay", "1");
    }

    const canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    badgeWrap.appendChild(canvas);

    let popover = null;
    let lastSpec = "";
    let ordCache = null;

    const drawBadge = (force = false) => {
        if (typeof window === "undefined" || !canvas.isConnected) {
            return;
        }
        const cs = window.getComputedStyle(badgeWrap);
        const fontSize = parseFloat(cs.fontSize) || 11;
        const lineHeight = parseFloat(cs.lineHeight) || 14;
        const fontFamily = cs.fontFamily || "sans-serif";
        const color = cs.color || "#7d8597";
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const fontSpec = `500 ${fontSize}px ${fontFamily}`;
        const specKey = `${fontSpec}|${lineHeight}|${color}|${dpr}`;

        if (!force && specKey === lastSpec) {
            return;
        }
        lastSpec = specKey;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        ctx.font = fontSpec;
        const measured = ctx.measureText(labelText);
        const cssWidth = Math.max(44, Math.ceil(measured.width + 2));
        const cssHeight = Math.max(14, Math.ceil(lineHeight));

        canvas.width = Math.round(cssWidth * dpr);
        canvas.height = Math.round(cssHeight * dpr);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssWidth, cssHeight);
        ctx.font = fontSpec;
        ctx.fillStyle = color;
        ctx.textBaseline = "middle";
        ctx.fillText(labelText, 1, cssHeight / 2 + 0.5);

        ctx.fillStyle = Math.random() > 0.5 ? "rgba(128,128,128,0.01)" : "rgba(128,128,128,0.015)";
        ctx.fillRect(0, 0, 1, 1);
    };

    const renderOrdDetails = (popEl, info) => {
        if (!popEl || !popEl.isConnected) {
            return;
        }
        while (popEl.firstChild) {
            popEl.removeChild(popEl.firstChild);
        }

        const infoLine = document.createElement("div");
        infoLine.textContent = (info && info.or) || meta.ord || meta.title || "Партнёрский материал Proglib";
        popEl.appendChild(infoLine);

        const resolvedErid = (info && info.er) || meta.erid || "";
        if (resolvedErid) {
            const tokenLine = document.createElement("div");
            tokenLine.style.opacity = "0.8";
            tokenLine.style.marginTop = "2px";
            tokenLine.textContent = `erid: ${resolvedErid}`;
            popEl.appendChild(tokenLine);

            const copyBtn = document.createElement("button");
            copyBtn.type = "button";
            copyBtn.setAttribute("data-p-copy", "1");
            copyBtn.textContent = "Скопировать токен";
            bindTrustedActivate(copyBtn, (copyEvent) => {
                if (!isTrustedActionEvent(copyEvent)) {
                    return;
                }
                copyEvent.preventDefault();
                copyEvent.stopPropagation();
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(String(resolvedErid)).catch(() => 0);
                }
                copyBtn.textContent = "Скопировано";
            }, true);
            popEl.appendChild(copyBtn);
        }
    };

    bindTrustedActivate(badgeWrap, (event) => {
        if (!isTrustedActionEvent(event)) {
            return;
        }
        if (popover && popover.contains(event.target)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        if (popover && popover.isConnected) {
            popover.parentNode.removeChild(popover);
            popover = null;

            return;
        }

        popover = document.createElement("div");
        popover.setAttribute("data-p-ord-pop", "1");
        bindTrustedActivate(popover, (popEvent) => {
            popEvent.stopPropagation();
        });
        badgeWrap.appendChild(popover);

        if (ordCache) {
            renderOrdDetails(popover, ordCache);

            return;
        }

        renderOrdDetails(popover, null);

        if (meta.id || meta.url) {
            const currentPop = popover;
            fetch(buildSlotUrl({
                op: "o",
                i: Number(meta.id) || 0,
                u: meta.url || ""
            }), {
                method: "GET",
                credentials: "same-origin"
            })
            .then((res) => res.json())
            .then((json) => {
                const unpacked = json && json.p ? decodePacket(json.p) : null;
                if (unpacked && unpacked.d) {
                    ordCache = unpacked.d;
                    renderOrdDetails(currentPop, ordCache);
                }
            })
            .catch(() => 0);
        }
    });

    if (meta.insertBeforeFirst && targetEl.firstChild) {
        targetEl.insertBefore(badgeWrap, targetEl.firstChild);
    } else {
        targetEl.appendChild(badgeWrap);
    }

    if (meta.shadowHandle && typeof meta.shadowHandle.registerBadge === "function") {
        meta.shadowHandle.registerBadge(drawBadge);
    }

    window.requestAnimationFrame(() => drawBadge(true));
    if (typeof document !== "undefined" && "fonts" in document && document.fonts.ready) {
        document.fonts.ready.then(() => drawBadge(true)).catch(() => 0);
    }

    return badgeWrap;
}

export function initPromoCards(scope = document) {
    pruneDisconnectedShadows();

    if (!scope || typeof scope.querySelectorAll !== "function") {
        return;
    }

    scope.querySelectorAll("[data-p-promo]").forEach((hostEl) => {
        if (boundPromoHosts.has(hostEl)) {
            return;
        }
        boundPromoHosts.add(hostEl);

        const wrapTarget = hostEl.closest("[data-p-promo-wrap]") || hostEl;
        if (wrapTarget && wrapTarget.hasAttribute("data-p-promo-wrap")) {
            wrapTarget.removeAttribute("data-p-promo-wrap");
        }

        const link = hostEl.dataset.pLink || "";
        const clickCookie = hostEl.dataset.clickCookie || "";
        hostEl.removeAttribute("data-p-promo");
        hostEl.removeAttribute("data-p-link");
        hostEl.removeAttribute("data-click-cookie");

        const shadowHandle = mountSlotShadow(hostEl, {
            active: true,
            wrapSelf: true
        });
        const card = shadowHandle.card;

        attachCanvasBadge(card, {
            overlay: true,
            url: link,
            shadowHandle
        });

        card.querySelectorAll("[data-p-close], [data-close-btn]").forEach((closeBtn) => {
            bindTrustedActivate(closeBtn, (event) => {
                if (!isTrustedActionEvent(event)) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                shadowHandle.destroy();
                wrapTarget.style.display = "none";
            }, true);
        });

        bindTrustedActivate(card, (event) => {
            if (!isTrustedActionEvent(event)) {
                event.preventDefault();

                return;
            }
            if (event.target && event.target.closest && event.target.closest("[data-p-close], [data-close-btn], [data-p-badge]")) {
                return;
            }
            if (clickCookie) {
                const maxAge = 60 * 60 * 24 * 30;
                document.cookie = `${clickCookie}=${clickCookie}; path=/; max-age=${maxAge}`;
            }
            if (link) {
                openTrustedUrl(event, link);
            }
        });
    });
}
